import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ScrollCheck } from "@/components/ScrollCheck";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { CTABanner } from "@/components/CTABanner";
import { SERVICE_CARD_PANEL_CLASS, SERVICE_CARD_WRAPPER_CLASS, SERVICES } from "@/lib/site";

const Services = () => {
  const cardRefs = useRef({});
  const [cardHeights, setCardHeights] = useState({});

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { entry.target.dataset.inView = String(entry.isIntersecting); });
    }, { threshold: 0.05 });
    Object.values(cardRefs.current).forEach((card) => { if (card) observer.observe(card); });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const updateHeights = () => {
      setCardHeights(
        SERVICES.reduce((heights, service) => {
          const card = cardRefs.current[service.slug];
          if (card) {
            heights[service.slug] = Math.ceil(card.getBoundingClientRect().height);
          }
          return heights;
        }, {})
      );
    };

    updateHeights();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateHeights);
      return () => window.removeEventListener("resize", updateHeights);
    }

    const observer = new ResizeObserver(updateHeights);
    SERVICES.forEach((service) => {
      const card = cardRefs.current[service.slug];
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div data-testid="services-page" className="relative isolate">
      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
          <Reveal className="max-w-3xl">
            <p className="section-eyebrow gold-hover-label text-sm uppercase tracking-[0.15em] font-semibold text-brand">Our Services</p>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-slate-900">
              Full life-cycle Core Banking services.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed">
              Whatever stage your core banking journey is at, we have the specialists and the
              accelerators to move you forward — safely and predictably.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SERVICE BLOCKS — sticky stacking deck */}
      <section className="services-deck relative isolate pb-24 md:pb-32" data-testid="services-deck">
        <div className="absolute inset-0 overflow-clip pointer-events-none" data-testid="services-backdrop" aria-hidden="true">
          <div className="services-background-image sticky top-0 h-[100svh] w-full">
            <img src={`${process.env.PUBLIC_URL}/media/Services_background.png`} alt="" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-white/75" />
          </div>
        </div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
          {SERVICES.map((s, i) => (
            <div
              key={s.slug}
              className={SERVICE_CARD_WRAPPER_CLASS}
              style={{
                "--service-card-height": `${cardHeights[s.slug] || 0}px`,
                top: `${100 + i * 18}px`,
                zIndex: i + 1,
              }}
            >
              <div
                id={s.slug}
                data-testid={`service-block-${s.slug}`}
                ref={(node) => {
                  cardRefs.current[s.slug] = node;
                }}
                className={SERVICE_CARD_PANEL_CLASS}
              >
                {/* Accent left bar */}
                <span className="absolute left-0 top-0 h-full w-1.5 bg-gradient-to-b from-brand to-brand-light" />
                {/* Decorative glow */}
                <div
                  className="pointer-events-none absolute -right-24 -top-24 w-72 h-72 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: "radial-gradient(circle, #E6F0FA, transparent 70%)" }}
                />
                <div className="relative grid lg:grid-cols-12 gap-8 items-start">
                  <div className="lg:col-span-7">
                    <div className="flex items-center gap-4">
                      <div className="gold-icon-tile w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-900/20 group-hover:scale-105 transition-transform shrink-0">
                        <s.icon className="w-7 h-7" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{s.title}</h2>
                      </div>
                    </div>
                    <p className="mt-6 text-lg text-slate-600 leading-relaxed">{s.description}</p>
                    <Link to="/contact" data-testid={`service-cta-${s.slug}`}>
                      <Button className="mt-7 rounded-full bg-brand hover:bg-brand-hover text-white font-semibold gap-2 px-6">
                        Discuss this service <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </Button>
                    </Link>
                  </div>
                  <div className="lg:col-span-5">
                    <div className="rounded-2xl bg-slate-50/80 border border-slate-100 p-6 group-hover:bg-brand-surface/60 group-hover:border-[#C8A45D]/40 transition-colors">
                      <p className="service-outcome-title gold-hover-label text-sm uppercase tracking-[0.15em] font-semibold text-brand">What this means to your organisation</p>
                      <ul className="mt-4 space-y-3.5">
                        {s.outcomes.map((o) => {
                          const outcome = typeof o === "string" ? { text: o } : o;
                          return (
                            <li key={`${outcome.label || ""}${outcome.text}`} className="gold-check-row flex items-start gap-3">
                              <ScrollCheck className="w-5 h-5 text-brand mt-0.5 shrink-0" />
                              <span className="text-slate-700 font-medium">
                                {outcome.label && <strong className="text-slate-900 font-semibold">{outcome.label} </strong>}
                                {outcome.text}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <CTABanner />
    </div>
  );
};

export default Services;
